import { type Accessor, createEffect, onCleanup } from "solid-js";
import type { Todo } from "@listto/powersync";
import type { updateTodoMutation } from "@listto/shared";
import { isTextEntryElement } from "@/lib/dom/editable";

interface UseTodoDetailKeyboardOptions {
  /** When false, the panel is mounted but inactive and must not capture keys. */
  isActive: Accessor<boolean>;
  containerRef: Accessor<HTMLDivElement | undefined>;
  getTodo: () => Todo;
  /** Opens the unified scheduling panel (due date / reminder / recurrence). */
  onOpenSchedule: () => void;
  onUpdate: (updates: Partial<Parameters<typeof updateTodoMutation>[2]>) => void;
  onToggleMyDay: (currentMyDay: string | null) => void;
  onToggleTodo: () => void;
  onRequestDelete: () => void;
  onGoToList?: () => void;
}

const SCROLL_STEP_PX = 80;

/**
 * Keyboard shortcuts and arrow-key focus navigation for the todo detail drawer.
 * Registers a document-level keydown listener while the panel is active.
 */
export function useTodoDetailKeyboard(o: UseTodoDetailKeyboardOptions) {
  const handleQuickActionShortcuts = (e: KeyboardEvent, keyLower: string): boolean => {
    if (keyLower === "i" || keyLower === "s") {
      e.preventDefault();
      e.stopPropagation();
      o.onUpdate({ important: o.getTodo().important === 1 ? 0 : 1 });
      return true;
    }
    if (keyLower === "d") {
      e.preventDefault();
      e.stopPropagation();
      o.onToggleMyDay(o.getTodo().my_day_date);
      return true;
    }
    // T (due date), R (reminder) and P (recurrence) all open the unified
    // scheduling panel, which hosts every one of those controls.
    if (keyLower === "t" || keyLower === "r" || keyLower === "p") {
      e.preventDefault();
      e.stopPropagation();
      o.onOpenSchedule();
      return true;
    }
    if (keyLower === "g") {
      e.preventDefault();
      e.stopPropagation();
      o.onGoToList?.();
      return true;
    }
    return false;
  };

  const handleOtherDrawerShortcuts = (
    e: KeyboardEvent,
    keyLower: string,
    isInteractable: boolean,
    container: HTMLElement,
  ): boolean => {
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      e.stopPropagation();
      o.onRequestDelete();
      return true;
    }
    if (e.key === " ") {
      if (isInteractable) {
        return false;
      }
      e.preventDefault();
      e.stopPropagation();
      o.onToggleTodo();
      return true;
    }
    if (keyLower === "a") {
      e.preventDefault();
      e.stopPropagation();
      const labelEl = container.querySelector("label[tabindex='0']");
      if (labelEl instanceof HTMLElement) {
        const fileInput = labelEl.querySelector("input[type='file']");
        if (fileInput instanceof HTMLInputElement) {
          fileInput.click();
        }
      }
      return true;
    }
    if (keyLower === "n") {
      e.preventDefault();
      e.stopPropagation();
      const notesEl = container.querySelector(".todo-notes-input");
      if (notesEl instanceof HTMLElement) {
        notesEl.focus();
      }
      return true;
    }
    if (keyLower === "y") {
      e.preventDefault();
      e.stopPropagation();
      const stepInputEl = container.querySelector(".todo-subtask-add-input");
      if (stepInputEl instanceof HTMLInputElement) {
        stepInputEl.focus();
      }
      return true;
    }
    return false;
  };

  const handleSingleKeyShortcuts = (
    e: KeyboardEvent,
    isInput: boolean,
    isInteractable: boolean,
    container: HTMLElement,
  ): boolean => {
    if (isInput || e.ctrlKey || e.metaKey || e.altKey) {
      return false;
    }

    const keyLower = e.key.toLowerCase();
    if (handleQuickActionShortcuts(e, keyLower)) {
      return true;
    }

    return handleOtherDrawerShortcuts(e, keyLower, isInteractable, container);
  };

  const handleArrowFocusNavigation = (
    e: KeyboardEvent,
    activeEl: HTMLElement,
    container: HTMLElement,
  ): boolean => {
    const isDown = e.key === "ArrowDown";
    const isUp = e.key === "ArrowUp";

    if (!isDown && !isUp) {
      return false;
    }

    const allFocusables = [
      ...container.querySelectorAll(
        'button, input:not([type="hidden"]), textarea, select, a[href], [tabindex="0"]',
      ),
    ];

    const focusables = allFocusables.filter((el): el is HTMLElement => {
      if (!(el instanceof HTMLElement)) {
        return false;
      }
      if (el.hasAttribute("disabled") || el.getAttribute("aria-disabled") === "true") {
        return false;
      }
      if (el.classList.contains("pointer-events-none") || el.closest(".pointer-events-none")) {
        return false;
      }
      if (el.classList.contains("sr-only")) {
        return false;
      }
      if (el.getAttribute("tabindex") === "-1") {
        return false;
      }
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        return false;
      }
      return true;
    });

    const currentIndex = focusables.indexOf(activeEl);
    if (currentIndex === -1) {
      return false;
    }

    if (isTextEntryElement(activeEl)) {
      const start = activeEl.selectionStart ?? 0;
      const end = activeEl.selectionEnd ?? 0;
      const valLen = activeEl.value.length;

      if (isDown && end < valLen) {
        return false;
      }
      if (isUp && start > 0) {
        return false;
      }
    }

    e.preventDefault();
    e.stopPropagation();
    let nextIndex = isDown ? currentIndex + 1 : currentIndex - 1;

    if (nextIndex >= focusables.length) {
      nextIndex = 0;
    } else if (nextIndex < 0) {
      nextIndex = focusables.length - 1;
    }

    const nextEl = focusables[nextIndex];
    if (nextEl) {
      nextEl.focus();
    }
    return true;
  };

  createEffect(() => {
    if (!o.isActive()) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      const container = o.containerRef();
      if (!(container instanceof HTMLElement)) {
        return;
      }

      if (e.ctrlKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        e.preventDefault();
        e.stopPropagation();
        const scrollable = container.querySelector(".overflow-y-auto") ?? container;
        const scrollAmount = e.key === "ArrowDown" ? SCROLL_STEP_PX : -SCROLL_STEP_PX;
        scrollable.scrollBy({ top: scrollAmount, behavior: "auto" });
        return;
      }

      const activeEl = document.activeElement;
      if (!activeEl) {
        return;
      }

      const isFocusInDrawer = container.contains(activeEl);

      const isInput =
        ["INPUT", "TEXTAREA"].includes(activeEl.tagName) ||
        (activeEl instanceof HTMLElement && activeEl.isContentEditable);

      const isInteractable =
        ["INPUT", "TEXTAREA", "BUTTON", "SELECT", "A"].includes(activeEl.tagName) ||
        (activeEl instanceof HTMLElement && activeEl.isContentEditable);

      if (
        (isFocusInDrawer || !isInput) &&
        handleSingleKeyShortcuts(e, isInput, isInteractable, container)
      ) {
        return;
      }

      if (isFocusInDrawer && activeEl instanceof HTMLElement) {
        void handleArrowFocusNavigation(e, activeEl, container);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    onCleanup(() => {
      document.removeEventListener("keydown", handleKeyDown);
    });
  });
}
